import "./styles.scss";
import * as React from "react";
import { Dropdown, Label, Modal, Pagination, Table } from "semantic-ui-react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  MetricFilters,
  MetricSummariesFilters,
  PlayerCard,
  PlayerCardMetric,
  PlayerCardMetricSummaries,
  PlayerCardServices,
  PlayerCardWidget,
} from "../../../services/bedrock";
import _ from "lodash";
import { DateRangePicker } from "@/components/DateRangePicker";
import TimeChart from "./TimeChart";
import PieChart from "./PieChart";
import { getWidgetTitle } from "../utils";
import { PlayerCardSettingsContext } from "../Contexts";

interface MetricsModalProps {
  open: boolean;
  playerCard?: PlayerCard;
  defaultWidgetId?: number;
  defaultMetricType?: "VIEW" | "CLICK";
  onClose: () => void;
}

const MetricsModal: React.FC<MetricsModalProps> = ({
  open,
  playerCard,
  defaultWidgetId,
  defaultMetricType,
  onClose,
}) => {
  const { User } = useContext(PlayerCardSettingsContext);

  const dateRanges = {
    LAST_7_DAYS: "Last 7 days",
    LAST_30_DAYS: "Last 30 days",
    ALL_TIME: "All time",
    CUSTOM: "Custom",
  };
  type DateRange = keyof typeof dateRanges;

  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);
  const last7Days = new Date(endOfToday);
  last7Days.setDate(last7Days.getDate() - 7);
  last7Days.setHours(0, 0, 0, 0);
  const last30Days = new Date(endOfToday);
  last30Days.setDate(last30Days.getDate() - 30);
  last30Days.setHours(0, 0, 0, 0);
  const beginningOfPlayerCard = new Date(playerCard?.createdDate || new Date());
  beginningOfPlayerCard.setHours(0, 0, 0, 0);

  const [metricSummaries, setMetricSummaries] = useState<PlayerCardMetricSummaries>();
  const [metrics, setMetrics] = useState<PlayerCardMetric[]>([]);
  const [metricsPage, setMetricsPage] = useState<number>(1);
  const [metricsTotalPages, setMetricsTotalPages] = useState<number>(1);
  const [isLoadingMetricSummaries, setIsLoadingMetricSummaries] = useState<boolean>(false);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState<boolean>(false);
  const [widgetId, setWidgetId] = useState<number | undefined>(defaultWidgetId);
  const [metricType, setMetricType] = useState<"VIEW" | "CLICK">(defaultMetricType ?? "VIEW");
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>("LAST_7_DAYS");
  const [startDate, setStartDate] = useState<Date>(last7Days);
  const [endDate, setEndDate] = useState<Date>(endOfToday);

  useEffect(() => {
    setMetricSummaries(undefined);
    setMetrics([]);
    const filters = {
      playerCardWidgetId: defaultWidgetId,
      metricType: defaultMetricType,
      page: 1,
    };
    loadData(filters);
  }, [open]);

  const getEffectiveFilters = useCallback(
    (filters: MetricFilters) => {
      const effectiveFilters = { ...filters };

      if (Object.hasOwn(effectiveFilters, "playerCardWidgetId")) setWidgetId(effectiveFilters.playerCardWidgetId);
      else effectiveFilters.playerCardWidgetId = widgetId;

      if (Object.hasOwn(effectiveFilters, "metricType")) setMetricType(effectiveFilters.metricType || metricType);
      else effectiveFilters.metricType = metricType;

      if (Object.hasOwn(effectiveFilters, "startDate")) setStartDate(effectiveFilters.startDate || startDate);
      else effectiveFilters.startDate = startDate;

      if (Object.hasOwn(effectiveFilters, "endDate")) setEndDate(effectiveFilters.endDate || endDate);
      else effectiveFilters.endDate = endDate;

      if (Object.hasOwn(effectiveFilters, "page")) setMetricsPage(effectiveFilters.page || metricsPage);
      else effectiveFilters.page = metricsPage;

      if (effectiveFilters.metricType === "VIEW") delete effectiveFilters.playerCardWidgetId;

      return effectiveFilters;
    },
    [widgetId, metricType, startDate, endDate]
  );

  const loadSummaries = useCallback(
    (filters: MetricSummariesFilters = {}) => {
      if (!playerCard || !open) return;

      const effectiveFilters = getEffectiveFilters(filters);

      setIsLoadingMetricSummaries(true);
      PlayerCardServices.getMetricSummaries(playerCard.id, effectiveFilters)
        .then(setMetricSummaries)
        .finally(() => setIsLoadingMetricSummaries(false));
    },
    [playerCard, open, getEffectiveFilters]
  );

  const loadMetrics = useCallback(
    (filters: MetricFilters = {}) => {
      if (!playerCard || !open) return;

      const effectiveFilters = getEffectiveFilters(filters);

      setIsLoadingMetrics(true);
      PlayerCardServices.getMetrics(playerCard.id, effectiveFilters)
        .then(response => {
          setMetrics(response.content);
          setMetricsTotalPages(response.pagination.totalPages);
        })
        .finally(() => setIsLoadingMetrics(false));
    },
    [playerCard, open, getEffectiveFilters]
  );

  const loadData = useCallback(
    (filters: MetricFilters = {}) => {
      loadSummaries(filters);
      loadMetrics(filters);
    },
    [loadSummaries, loadMetrics]
  );

  const DevicePieChart = useMemo(() => {
    if (metricSummaries === undefined) return null;

    var group = _.groupBy(metricSummaries.userAgentSummary, (record: any) => record.deviceClass || "Unknown");
    const data = Object.keys(group).map(key => ({ id: key, label: key, value: _.sumBy(group[key], "total") }));
    return <PieChart data={data} title="Devices" />;
  }, [metricSummaries?.userAgentSummary]);

  const StatePieChart = useMemo(() => {
    if (metricSummaries === undefined) return null;

    var group = _.groupBy(metricSummaries.ipAddressSummary, (record: any) => record.state || "Unknown");
    const data = Object.keys(group).map(key => ({ id: key, label: key, value: _.sumBy(group[key], "total") }));
    return <PieChart data={data} title="States" />;
  }, [metricSummaries?.ipAddressSummary]);

  const CityPieChart = useMemo(() => {
    if (metricSummaries === undefined) return null;

    var group = _.groupBy(
      metricSummaries.ipAddressSummary,
      (record: any) => `${record.city || "Unknown"}, ${record.state || "Unknown"}`
    );
    const data = Object.keys(group).map(key => ({ id: key, label: key, value: _.sumBy(group[key], "total") }));
    return <PieChart data={data} title="Cities" />;
  }, [metricSummaries?.ipAddressSummary]);

  const getMetricWidgetName = (metric: PlayerCardMetric) => {
    if (metric.playerCardWidgetId === null) return "-";

    const widget = playerCard?.widgets.find(w => w.id === metric.playerCardWidgetId);
    if (widget) {
      return getWidgetName(widget);
    } else if (metric.widgetType) {
      return getWidgetNameContainer(getWidgetTitle(metric.widgetType), "Deleted Widget");
    } else {
      return <span>Deleted Widget</span>;
    }
  };

  const getWidgetName = (widget: PlayerCardWidget) => {
    return getWidgetNameContainer(getWidgetTitle(widget.owner.type), `Widget #${(widget.sortIndex ?? -1) + 1}`);
  };

  const getWidgetNameContainer = (title: string, subtitle: string) => {
    return (
      <div className="widgetNameContainer">
        <b>{title}</b> <span>({subtitle})</span>
      </div>
    );
  };

  const getTagsView = (metric: PlayerCardMetric) => {
    const tags = metric.tags ? JSON.parse(metric.tags) : {};

    const supportedTags: { [key: string]: string } = {};
    if (metric.metricType === "VIEW") {
      supportedTags.source = "Source";
    } else {
      Object.keys(tags).forEach(key => {
        supportedTags[key] = key;
      });
      if (metric.widgetType === "ContentPage") {
        supportedTags.contentPageId = "Video Page ID";
      } else if (metric.widgetType === "Post") {
        supportedTags.postId = "Post ID";
        supportedTags.socialNetwork = "Social Network";
      } else if (metric.widgetType === "SocialMedia") {
        supportedTags.socialNetwork = "Social Network";
      } else if (metric.widgetType === "Link") {
        supportedTags.url = "URL";
      } else if (metric.widgetType === "Address") {
        supportedTags.address = "Address";
      } else if (metric.widgetType === "Phone") {
        supportedTags.phoneNumber = "Phone Number";
      } else if (metric.widgetType === "Email") {
        supportedTags.email = "Email";
      } else if (metric.widgetType === "SmartLink") {
        supportedTags.smartLinkId = "Smart Link ID";
      } else if (metric.widgetType === "Review") {
        supportedTags.reviewPageLink = "Reviews Page";
        supportedTags.reviewOpened = "Review Opened";
        supportedTags.reviewSourceLink = "Review Source Link";
      }
    }
    if (Object.keys(supportedTags).length === 0) return null;

    return (
      <div className="tagsContainer">
        {Object.keys(supportedTags).map(key => (
          <Label key={key} image>
            {supportedTags[key]}
            <Label.Detail>{tags[key] ?? "-"}</Label.Detail>
          </Label>
        ))}
      </div>
    );
  };

  const handleWidgetChange = useCallback(
    (_: any, { value }: any) => {
      var playerCardWidgetId: number | undefined = parseInt(value);
      if (isNaN(playerCardWidgetId)) {
        playerCardWidgetId = undefined;
      }
      loadData({ playerCardWidgetId, page: 1 });
    },
    [loadData]
  );

  const handleMetricTypeChange = useCallback(
    (_: any, { value }: any) => {
      loadData({ metricType: value as "VIEW" | "CLICK", page: 1 });
    },
    [loadData]
  );

  const handleDateRangeChange = useCallback(
    (_: any, { value }: any) => {
      setSelectedDateRange(value as DateRange);

      var startDate: Date;
      var endDate: Date;
      if (value === "LAST_7_DAYS") {
        startDate = last7Days;
        endDate = endOfToday;
      } else if (value === "LAST_30_DAYS") {
        startDate = last30Days;
        endDate = endOfToday;
      } else if (value === "ALL_TIME") {
        startDate = beginningOfPlayerCard;
        endDate = endOfToday;
      } else {
        return;
      }
      loadData({ startDate, endDate, page: 1 });
    },
    [loadData]
  );

  const handleChangeStartDate = useCallback(
    (date?: Date) => {
      if (!date) return;

      loadData({ startDate: date });
    },
    [loadData]
  );

  const handleChangeEndDate = useCallback(
    (date?: Date) => {
      if (!date) return;

      loadData({ endDate: date });
    },
    [loadData]
  );

  const handlePageChange = useCallback(
    (_: any, data: any) => {
      loadMetrics({ page: Number(data.activePage) });
    },
    [loadMetrics]
  );

  const metricTypeOptions = [
    { key: "VIEW", value: "VIEW", text: "Views" },
    { key: "CLICK", value: "CLICK", text: "Clicks" },
  ];
  const widgetOptions = useMemo(() => {
    return playerCard?.widgets.map(widget => ({ key: widget.id, text: getWidgetName(widget), value: widget.id }));
  }, [playerCard?.widgets]);
  const dateRangeOptions = Object.keys(dateRanges).map(k => {
    const key = k as DateRange;
    return { key: k, text: dateRanges[key], value: key };
  });

  const metricTypeFriendlyString = _.startCase(_.capitalize(metricType));

  return (
    <Modal open={open} size="large" className="playerCardMetricsModal" onClose={onClose} closeIcon={true}>
      <Modal.Header>
        Player Card Metrics {User.isCoach && playerCard && `for ${playerCard.player.fullName}`}
      </Modal.Header>
      <Modal.Content className="metricsModalContent">
        <div className="chartFilters">
          <div className="chartFilter">
            <span className="chartFilterTitle">Metric Type</span>
            <Dropdown
              placeholder="Metric Type"
              selection
              value={metricType}
              options={metricTypeOptions}
              onChange={handleMetricTypeChange}
            />
          </div>
          {metricType === "CLICK" && (
            <div className="chartFilter">
              <span className="chartFilterTitle">Widget</span>
              <Dropdown
                placeholder="Widget"
                selection
                clearable
                value={widgetId}
                options={widgetOptions}
                onChange={handleWidgetChange}
              />
            </div>
          )}
          <div className="chartFilter">
            <span className="chartFilterTitle">Date Range</span>
            <div className="dateRangeInputs">
              <Dropdown
                placeholder="Date Range"
                selection
                value={selectedDateRange}
                options={dateRangeOptions}
                onChange={handleDateRangeChange}
              />
              {selectedDateRange === "CUSTOM" && (
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  minDate={beginningOfPlayerCard}
                  maxDate={endOfToday}
                  onChangeStartDate={handleChangeStartDate}
                  onChangeEndDate={handleChangeEndDate}
                />
              )}
            </div>
          </div>
        </div>

        {(!metricSummaries || metricSummaries.total === 0) && (
          <div className="zeroState">{isLoadingMetricSummaries ? "Loading..." : "No metrics found"}</div>
        )}
        {metricSummaries && metricSummaries.total > 0 && (
          <div className={`metricsContainer ${isLoadingMetricSummaries ? "loading" : ""}`}>
            <div>
              <b>Total {metricTypeFriendlyString}s:</b> {metricSummaries.total}
            </div>
            <div className="charts">
              <TimeChart
                dailySummary={metricSummaries.dailySummary}
                start={startDate}
                end={endDate}
                metricType={metricTypeFriendlyString}
              />
            </div>
            <div className="charts">
              {DevicePieChart}
              {StatePieChart}
              {CityPieChart}
            </div>
            {metrics.length && (
              <>
                <Table celled className="metricsTable" disabled={isLoadingMetrics}>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Time</Table.HeaderCell>
                      {metricType !== "VIEW" && <Table.HeaderCell>Widget</Table.HeaderCell>}
                      <Table.HeaderCell>Device</Table.HeaderCell>
                      <Table.HeaderCell>Location</Table.HeaderCell>
                      <Table.HeaderCell>Tags</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {metrics.map(metric => (
                      <Table.Row key={metric.id}>
                        <Table.Cell>{new Date(metric.createdDate).toLocaleString()}</Table.Cell>
                        {metricType !== "VIEW" && <Table.Cell>{getMetricWidgetName(metric)}</Table.Cell>}
                        <Table.Cell>
                          {metric.userAgent.deviceName}, {metric.userAgent.osName}
                        </Table.Cell>
                        <Table.Cell>{`${metric.ipAddress.city || "Unknown"}, ${
                          metric.ipAddress.state || "Unknown"
                        }`}</Table.Cell>
                        <Table.Cell>{getTagsView(metric)}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
                <Pagination
                  activePage={metricsPage}
                  totalPages={metricsTotalPages}
                  disabled={isLoadingMetrics}
                  pointing
                  secondary
                  siblingRange={3}
                  firstItem={null}
                  lastItem={null}
                  onPageChange={handlePageChange}
                />
              </>
            )}
          </div>
        )}
      </Modal.Content>
    </Modal>
  );
};
export default MetricsModal;
